import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { DataFileImportAdminMode, DataFileImportFormType, ItemMapFormType, dateColumnTypeItems, fileContainsItems } from "../DataFileImportEditorUtils";
import { ContentContainer } from "../../../../components/content-container/ContentContainer";
import { ContentSection } from "../../../../components/content-section/ContentSection";
import FormPropertySection from "../../../../components/forms/form-property-section/FormPropertySection";
import FormProperty from "../../../../components/forms/form-property/FormProperty";
import FormInput from "../../../../components/forms/form-input/FormInput";
import { useConfirmation } from "../../../../contexts/confirmation-context/ConfirmationContext";
import DataFileImportDataService from "../../../../services/data-file-import-data-service/DataFileImportDataService";
import { RestError } from "../../../../services/rest-service/RestService";
import { AppNotificationTypeEnum } from "../../../../contexts/app-notification-context/AppNotificationContextTypes";
import { useAppNotificationContext } from "../../../../contexts/app-notification-context/UseAppNotificationContext";
import _ from "lodash";
import { DateColumnTypeEnum, FileContainsTypeEnum, ImportTypeEnum } from "../../../../services/data-file-import-data-service/Models";
import FormSelect from "../../../../components/forms/form-select/FormSelect";
import { MenuItem, Stack } from "@mui/material";
import { eLynxObjectTypeEnum } from "../../../../common/enums/eLynxObjectType";
import FormCheckbox from "../../../../components/forms/form-checkbox/FormCheckbox";
import FormRadioGroup from "../../../../components/forms/form-radio-group/FormRadioGroup";
import { useQueryNotifications } from "../../../../queries/UseQueryNotifications";
import { useTimezones } from "../../../../queries/time-zones/UseTimezones";
import { MAX_FILE_SIZE_BYTES } from "../../DataFileImportUtils";
import { SetPointEventTypeEnum } from "../../../../common/enums/SetPointEventTypeEnum";


export type FileInformationProps = {
    groupID: number
};


export default function FileInformation({
    groupID
}: FileInformationProps) {

    const { t } = useTranslation();
    const [, setAppNotificationState ] = useAppNotificationContext()
    const { confirm /*, acknowledge*/ } = useConfirmation();

    const { data: timezones } = useQueryNotifications(useTimezones)();    

    const {
        getValues,
        watch,
        //trigger,
        control,
        //reset,
        setValue,
        //resetField
    } = useFormContext<DataFileImportFormType>();    

    const importForm = useWatch({ control: control }) as DataFileImportFormType;

    const [columns,
        fileName
    ] = watch([
        'columns',
        'fileName'
    ]);

    const hasFile = fileName.length > 0;
    const columnOptions = _.cloneDeep(columns);

    const timeColumnOptions = columnOptions.filter(o => o.name !== importForm.dateColumn.name);

    const mode = (importForm.id ?? 0) <= 0 ?
                        DataFileImportAdminMode.Add : 
                        DataFileImportAdminMode.Edit;

    async function getFileTooLargeConfirmation() {
        await confirm({
            dialogTitle: t("dataFileImport.maxFileSizeExceededTitle"),
            confirmationText: t("dataFileImport.maxFileSizeExceeded"),
            yesButtonText: t("buttons.okay"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: false
        });
    }

    async function onFileChanged(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) : Promise<void> 
    {
        const files = (e as React.ChangeEvent<HTMLInputElement>)?.target?.files;
        if (files && files.length > 0) {

            const file = files[0];

            if (file.size < MAX_FILE_SIZE_BYTES) {

                const hasHeaderRow = getValues('fileHasHeaderRow');

                try {

                    const fileInfo = await DataFileImportDataService.configSaveCsvFile(groupID, {
                                fileHasHeaderRow: hasHeaderRow
                            }, 
                            file);

                    setValue('columns', fileInfo.columns, {shouldDirty: true});

                    setValue('fileStoreInfo', fileInfo.fileStoreInfo);

                } catch(error) {

                    const restError = error as RestError;
                    setAppNotificationState({
                        notificationType: AppNotificationTypeEnum.Error,
                        show: true,
                        title: "CSV File Error",
                        message: restError.response?.data?.message,
                        error: restError,
                        hideContactInfo: true
                    });
                }
            } else {
                await getFileTooLargeConfirmation();

                setTimeout(() => {
                    setValue('fileName', "", {
                        shouldDirty: true,
                        shouldValidate: true
                    });
                }, 0);
            }
        }

        if (mode === DataFileImportAdminMode.Add) {
            resetFields();
        } else {
            updateFields();
        }


        e.preventDefault();
        //return name;
    }


    function onFileContainsChanged() {

        const value = getValues('fileContainsType');

        let objectType: eLynxObjectTypeEnum = eLynxObjectTypeEnum.None;
        let importType: ImportTypeEnum = ImportTypeEnum.None;

        switch (value) {
            case FileContainsTypeEnum.DataForSingleDevice:
                objectType = eLynxObjectTypeEnum.Device;
                importType = ImportTypeEnum.Single;
                break;

            case FileContainsTypeEnum.DataForMultipleDevices:
                objectType = eLynxObjectTypeEnum.Device;
                importType = ImportTypeEnum.Multiple;
                break;
    
            case FileContainsTypeEnum.DataForSingleTank:
                objectType = eLynxObjectTypeEnum.Tank;
                importType = ImportTypeEnum.Single;
                break;

            case FileContainsTypeEnum.DataForMultipleTanks:
                objectType = eLynxObjectTypeEnum.Tank;
                importType = ImportTypeEnum.Multiple;
                break;

            case FileContainsTypeEnum.DataForSingleWell:
                objectType = eLynxObjectTypeEnum.Well;
                importType = ImportTypeEnum.Single;
                break;

            case FileContainsTypeEnum.DataForMultipleWells:
                objectType = eLynxObjectTypeEnum.Well;
                importType = ImportTypeEnum.Multiple;
                break;

            default:
                break;
        }

        setValue('importObjectType', objectType, {shouldDirty: true});
        setValue('importType', importType, {shouldDirty: true});


        resetFields();
    }

    function onDateColumnTypeChanged() {
        setValue('timeColumn', {
            id: -1,
            name: '',
        }, {
            shouldDirty: true
        });

        resetFields();
    }

    function onDateColumnChanged() {

        resetFields();
    }

    function resetFields() {
        setValue('identifierColumn', {
            id: -1,
            name: ''
        }, {
            shouldDirty: true
        });
        setValue('objectMap', [], {shouldDirty: true});
        setValue('tagTypeMap', [], {shouldDirty: true});
        setValue('tagTypeColumns', [], {shouldDirty: true});
    }

    function updateFields() {

        const [
            updatedColumns,
            //objectMap,
            tagTypeMap,
            identiferColumn
         ] = getValues([
            'columns',
            //'objectMap',
            'tagTypeMap',
            'identifierColumn'
        ]);

        const updatedTagTypeMap: ItemMapFormType[] = [];

        tagTypeMap.forEach(o => {
            // mapped column still exists
            if (updatedColumns.find(v => v.name === o.column)) {

                updatedTagTypeMap.push({...o,
                    eventType: o.eventType ? o.eventType : SetPointEventTypeEnum.Imported
                });
            }
        });

        setValue('tagTypeMap', updatedTagTypeMap, {shouldDirty: true});

        if (!updatedColumns.find(o => o.name === identiferColumn?.name)) {

            setValue('identifierColumn', {
                id: -1,
                name: ''
            }, {
                shouldDirty: true,
                shouldValidate: true
            });

            setValue('objectMap', 
                [], {
                shouldDirty: true,
                shouldValidate: true
            });
        }
        
    }

    function validateNotEmpty(value: string) {
        return value && value.length > 0;
    }

   return (
       <ContentContainer>
           <ContentSection sx={{
               padding: 1,
               overflowY: 'auto'
           }}>
           
           <FormPropertySection label={t('dataFileImport.steps.fileInformation')}>

                   <FormProperty>
                       <FormInput
                           name="fileName"
                           label={t("attachmentDialog.file")}
                           multiline={false}
                           //disabled={mode === DataFileImportAdminMode.Edit}
                           sx={{
                               marginTop: 2,
                               width: 1,
                               marginBottom: 1
                           }}
                           rules={{
                               required: true,
                               validate: validateNotEmpty
                           }}
                           fileUpload
                           fileAcceptTypes=".csv"
                           onChange={onFileChanged}
                           helperText={t('dataFileImport.validcsv')}
                       />

                        {/* <Alert severity="info">
                            {t('dataFileImport.validcsv')}
                        </Alert> */}


                   </FormProperty>

               </FormPropertySection>

               <FormPropertySection label={t('dataFileImport.dataLayout')}>
                   <FormProperty>
                       <FormSelect
                           name="fileContainsType"
                           label={t('dataFileImport.fileContains')}
                           //disabled={mode === DataFileImportAdminMode.Edit}
                           sx={{ 
                               width: 1,
                               marginTop: 1,
                               maxWidth: '400px'
                           }}
                           onChange={() => onFileContainsChanged()}
                       >
                           {
                               fileContainsItems.map((o) => 
                                   <MenuItem key={`fc.${o.value}`} value={o.value}>
                                       {o.label}
                                   </MenuItem>
                               )
                           }
                       </FormSelect> 
                   </FormProperty>

               </FormPropertySection>

               <FormPropertySection label={t('dataFileImport.options')}>
                   <FormProperty>

                       <FormCheckbox
                           name="fileHasHeaderRow"
                           label={t("dataFileImport.fileHasHeader")}
                           //disabled={mode === DataFileImportAdminMode.Edit}
                           onChange={() => resetFields()}
                       />

                   </FormProperty>

               </FormPropertySection>

               <FormPropertySection label={t('dataFileImport.dateTimeInfo')}>

                   <Stack direction='column' sx={{
                       flex: '1 1 auto'
                   }}>

                       <FormRadioGroup
                           name='dateColumnType'
                           label=''
                           direction='row'
                           radioButtons={dateColumnTypeItems} 
                           //disabled={!hasFile || mode === DataFileImportAdminMode.Edit}
                           disabled={!hasFile}
                           onChange={() => onDateColumnTypeChanged()}
                       />


                       <FormSelect
                           name="dateColumn.name"
                           label={t(importForm.dateColumnType === DateColumnTypeEnum.One ? 'dataFileImport.dateColumnPrompt' : 'dataFileImport.dateColumn')}
                           sx={{ 
                               width: 1,
                               marginTop: 1,
                               maxWidth: '400px'
                           }}
                           //readOnly={mode === DataFileImportAdminMode.Edit}
                           disabled={!hasFile || columns.length === 0}
                           onChange={() => onDateColumnChanged()}
                       >
                           <MenuItem value='' disabled={true}>
                               
                           </MenuItem>
                           {
                               columnOptions.map((o) => 
                                   <MenuItem key={`datecol.${o.id}`} value={o.name}>
                                       {o.name}
                                   </MenuItem>
                               )
                           }

                       </FormSelect>                         

                       {importForm.dateColumnType === DateColumnTypeEnum.Two &&

                           <FormSelect
                               name="timeColumn.name"
                               label={t('dataFileImport.timeColumnPrompt')}
                               sx={{ 
                                   width: 1,
                                   marginTop: 1,
                                   maxWidth: '400px'
                               }}
                               //readOnly={mode === DataFileImportAdminMode.Edit}
                               disabled={!hasFile || timeColumnOptions.length === 0}
                               onChange={() => onDateColumnChanged()}
                               >
                               <MenuItem value='' disabled={true}>
                                   
                               </MenuItem>
                               {
                                   timeColumnOptions.map((o) => 
                                       <MenuItem key={`timecol.${o.id}`} value={o.name}>
                                           {o.name}
                                       </MenuItem>
                                   )
                               }

                           </FormSelect>                         
                       }

                   </Stack>

               </FormPropertySection>

               <FormPropertySection label=''>
                   <FormSelect
                           name="timezoneIndex"
                           label={t('dataFileImport.timezoneLabel')}
                           sx={{ 
                               width: 1,
                               marginTop: 1,
                               maxWidth: '400px'
                           }}
                       >
                           {
                               timezones?.map((o) => 
                                   <MenuItem key={`tz.${o.id}`} value={o.id}>
                                       {o.name}
                                   </MenuItem>
                               )
                           }
                       </FormSelect>                      

               </FormPropertySection>

           </ContentSection>
       </ContentContainer>
   );
  }
